import React, { ChangeEvent, PureComponent } from 'react';
import { Field, WrappedFieldInputProps } from 'redux-form';
import moment from 'moment';

import {
  Box,
  createStyles,
  FormControl,
  FormLabel,
  InputAdornment,
  Tab,
  Tabs,
  Theme,
  withStyles,
  WithStyles,
  TextField,
  Drawer,
  IconButton,
  ListItem,
  ListItemText,
  List
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import locales from '../../locales';
import TextFieldComp from '../Common/TextFieldComp';
import TextAreaComp from '../Common/TextAreaComp';
import PhoneFieldComp from './PhoneField';
import CircleCheckBox from '../Common/CircleCheckBox';
import { reserveModal } from '../../config/constants';
import { TEditReserveInfo } from '../../containers/ReservationPageEditCont/IReserveEditPage';
import { TConfirmReserveInfo } from '../../containers/ReservationPageConfirmCont/IReserveConfirmPage';
import ReserveSectionSelect from './ReserveSectionSelect';
import { IPropsSelectOption } from '../../reducers/selectors';
import { IWorkTime } from '../../types/IScheduleStore';

import ReserveDatePicker from '../Common/DatePickers/ReserveDatePicker';
import ReservePersonSelect from './ReservePersonSelect';
import ReserveTimeSelect from './ReserveTimeSelect';
import { TUpdateReserveInfo } from '../../types/IReserveModalStore';
import { parseDateForReq } from '../../helpers/dateHelpers';
import { normalizePositiveNumber } from '../../_FSD/shared/lib';
import normalizeDurationHours from "../../helpers/normalizeDurationHours";
import Button from '@material-ui/core/Button';

const StyledTab = withStyles({
  root: {
    minWidth: 80,
    fontSize: '16px',
  },
})(Tab);

const styles = (theme: Theme) => createStyles({
  wrap: {
    weight: '40%',
    backgroundColor: '#FFFFFF',
    overflow: 'scroll',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    // height: 'calc(100vh - 226px)',
    height: 'calc(100vh - 203px)',
    width: '100%',
    flexShrink: 0,
    maxWidth: 254,
    minWidth: 230,
    [theme.breakpoints.down(960)]: {
      maxWidth: '100%',
      minWidth: 230,
    },
    [theme.breakpoints.down(465)]: {
      maxWidth: '100%',
      minWidth: '100%',
      paddingBottom: 16,
      height: '100%',
      flex: 1,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
    },
  },
  wrapContent: {
    padding: '0 24px',
    width: '100%',
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down(465)]: {
      padding: '0 16px',
    },
  },
  tabs: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFFFFF',
    boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.16)',
    zIndex: 1,
  },
  fieldStyle: {
    paddingTop: 24,
  },
  durationFieldStyle: {
    display: 'flex',
  },
  fieldEdit: {
    paddingTop: 30,
  },
  mobileTableButton: {
    marginTop: theme.spacing(2),
    width: '100%',
  },
});

export type IViewType = 'table' | 'scheme';

interface IProps extends WithStyles<typeof styles> {
  reserveInfo?: TEditReserveInfo | TConfirmReserveInfo | TUpdateReserveInfo;
  getFreeTables: (field: { value: string | number, type: string }) => void;
  getCalculatedPrepayAmount?: (field: { value: string | number, type: string }) => void;
  isMergeTables: boolean;
  sections: Array<IPropsSelectOption>;
  handleMergeTables: () => void;
  handleErrorMsg?: (msg: string) => void;
  minutes?: number;
  type?: 'online' | 'new' | 'edit';
  locale?: string;
  viewType: IViewType;
  onViewChange: (table: IViewType) => void;

  timeList: Array<IWorkTime>;
  maxCapacity: number;

  onReservePropertyChange: (name: string, value: any) => void;
  disabled?: boolean;
  isBanquet: boolean;
  activeTables?: { [id: number]: any };
  freeTables?: Array<any> | void;
  onOpenTableSelectModal?: () => void;
  section?: number;
  handleSelectTable?: (table: any) => void;
  formValues?: {
    date?: any;
    time?: string;
    persons?: number;
  };
  handleViewChange?: (sectionId: number) => void;
  isMobile?: boolean; // Для fullscreen режима (ширина < 600px или высота < 600px)
  isMobileLayout?: boolean; // Для мобильной верстки (только ширина < 600px)
}

class ReservesDataFields extends PureComponent<IProps> {
  state = {
    selectOpen: false,
    drawerOpen: false, // Состояние для открытия Drawer на мобильных (множественный выбор)
    drawerOpenSingle: false, // Состояние для открытия Drawer на мобильных (одиночный выбор)
  };

  componentDidUpdate(prevProps: IProps) {
    const {
      formValues,
      freeTables,
      sections,
      section,
      handleViewChange
    } = this.props;

    // Проверяем, что дата, время и количество гостей заполнены
    const date = formValues?.date;
    const time = formValues?.time;
    const persons = formValues?.persons;
    const emptyTime = '-';
    const isFieldsFilled = date && date !== emptyTime && time && time !== emptyTime && Boolean(persons);

    // Проверяем, изменились ли условия для автоматического выбора зала
    const prevDate = prevProps.formValues?.date;
    const prevTime = prevProps.formValues?.time;
    const prevPersons = prevProps.formValues?.persons;
    const prevIsFieldsFilled = prevDate && prevDate !== emptyTime && prevTime && prevTime !== emptyTime && prevPersons && prevPersons > 0;
    const prevSection = prevProps.section;
    const freeTablesChanged = freeTables !== prevProps.freeTables;

    // Автоматически выбираем первый зал с подходящими столами, если:
    // 1. Поля заполнены
    // 2. Зал не выбран (section === 0 или undefined)
    // 3. Есть доступные столы (свободные И с достаточной вместительностью)
    // 4. Либо поля только что заполнились, либо изменились доступные столы
    if (isFieldsFilled && (!section || section === 0) && freeTables && Array.isArray(freeTables) && sections && sections.length > 0 && handleViewChange) {
      // Выбираем зал только если условия изменились
      if ((!prevIsFieldsFilled && isFieldsFilled) || (freeTablesChanged && prevSection === section)) {
        // Находим первый зал, в котором есть подходящие столы
        // Столы должны быть:
        // 1. В freeTables (уже свободные, полученные с сервера)
        // 2. С достаточной вместительностью (capacity >= persons)
        for (const sectionOption of sections) {
          if (sectionOption.value === 0) continue; // Пропускаем "Все залы"

          // Фильтруем столы: только те, что в freeTables (свободные) И с достаточной вместительностью
          const sectionTables = freeTables.filter(table => {
            // Проверка вместительности
            const hasCapacity = !persons || table.capacity >= persons;
            if (!hasCapacity) {
              return false;
            }
            // Проверка принадлежности к залу
            return table.section && table.section.id === sectionOption.value;
          });

          // Выбираем зал только если есть хотя бы один подходящий стол
          if (sectionTables.length > 0) {
            handleViewChange(sectionOption.value);
            break;
          }
        }
      }
    }
  }

  onPropertyChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const { onReservePropertyChange } = this.props;

    onReservePropertyChange(name, value);
  }

  render() {
    const {
      disabled,
      reserveInfo,
      classes,
      getFreeTables,
      isMergeTables,
      sections,
      handleMergeTables,
      handleErrorMsg,
      locale,
      viewType,
      onViewChange,
      timeList,
      maxCapacity,
      isBanquet,
      getCalculatedPrepayAmount,
      activeTables,
      freeTables,
      onOpenTableSelectModal,
      section,
      handleSelectTable,
      formValues,
    } = this.props;

    // Определение размера экрана для мобильных
    // Для мобильной верстки (скрытие табов, показ кнопки "Выбрать на схеме")
    // используем isMobileLayout (только ширина < 960px)
    // Для fullscreen режима используем isMobile (ширина < 960px или высота < 600px)
    const isClient = typeof window !== 'undefined';
    const isMobileLayout = this.props.isMobileLayout !== undefined
      ? this.props.isMobileLayout
      : (isClient && window.innerWidth < 600);
    const isMobile = this.props.isMobile !== undefined
      ? this.props.isMobile
      : (isClient && window.innerWidth < 600);

    // Проверяем, что дата, время и количество гостей заполнены
    const date = formValues?.date;
    const time = formValues?.time;
    const persons = formValues?.persons;
    const emptyTime = '-';
    const isFieldsFilled = date && date !== emptyTime && time && time !== emptyTime && Boolean(persons);

    // Получаем список выбранных столов
    const selectedTables = activeTables ? Object.values(activeTables) : [];

    // Фильтруем столы по выбранному залу, показываем только доступные и проверяем вместительность
    // Важно: freeTables уже содержит только свободные столы (полученные с сервера),
    // но мы дополнительно фильтруем по вместительности, так как сервер может вернуть
    // столы, которые не подходят по вместительности (например, если запрос был сделан с persons=0)
    // В режиме "Объединить столы" проверка вместительности не нужна
    const availableTables = (freeTables && Array.isArray(freeTables) && isFieldsFilled)
      ? freeTables.filter(table => {
        // Проверка вместительности: стол должен вмещать всех гостей
        // В режиме "Объединить столы" (isMergeTables === true) проверку пропускаем
        if (!isMergeTables && Boolean(persons)) {
          const hasCapacity = table.capacity >= persons;
          if (!hasCapacity) {
            return false;
          }
        }

        // Если зал не выбран (section === 0 или undefined), показываем все подходящие столы
        if (!section || section === 0) {
          return true;
        }
        // Фильтруем по залу
        return table.section && table.section.id === section;
      })
      : [];

    // Получаем выбранные столы для отображения в селекте
    // В режиме "Объединить столы" - массив ID для мультивыбора
    // В обычном режиме - один ID
    const selectedTableIds = isMergeTables
      ? selectedTables.map(t => t.id)
      : (selectedTables.length > 0 ? [selectedTables[0].id] : []);

    const firstName = reserveInfo ? reserveInfo.firstName : null;
    // const lastName = reserveInfo ? reserveInfo.lastName : null;

    const phone = reserveInfo ? reserveInfo.phone : null;
    const type = (reserveInfo && 'type' in reserveInfo && reserveInfo.type) || reserveModal.NEW;

    const canEdited = reserveInfo && type !== reserveModal.CONFIRM;

    // TODO Костыль на запрет редактирования банкетов
    const disabledEditBanquet = type === 'edit' && isBanquet;

    const banquetCheckBox = ({ input }: { input: WrappedFieldInputProps }) => (
      <CircleCheckBox
        label={locales.t('common.reserveModal.banquetCheckBox')}
        value={input.value}
        onChange={isBanquet && handleErrorMsg
          ? () => handleErrorMsg(locales.t('common.editReserveModal.banquetErrorMsg'))
          : input.onChange}
        isClickableLabel
        disabled={disabled || disabledEditBanquet}
      />
    );

    const readonly = reserveInfo
      && 'type' in reserveInfo
      && reserveInfo.type === reserveModal.CONFIRM;

    const normalizeToNearest5 = (value: string): string => {
      const numericValue = Number(value);
      if (isNaN(numericValue)) return '';

      return String(Math.round((numericValue > 55 ? 55 : numericValue) / 5) * 5);
    };

    return (
      <div
        className={classes.wrap}
        style={isMobile ? { height: '100%', flex: 1 } : undefined}
      >
        {/* Табы схема/список - скрыты только на мобильных (ширина < 600px) */}
        {!isMobileLayout && (
          <Tabs
            className={classes.tabs}
            value={viewType}
            onChange={(event: any, value) => onViewChange(value)}
            scrollButtons="off"
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            centered
          >
            <StyledTab
              label={locales.t('common.reserveModal.list')}
              value="table"
            />
            <StyledTab
              label={locales.t('common.reserveModal.schema')}
              value="scheme"
            />
          </Tabs>
        )}
        <div className={classes.wrapContent}>
          <div style={{ paddingTop: 16 }}>
            {(readonly && (
              <TextField
                fullWidth
                name="persons"
                disabled
                value={reserveInfo && reserveInfo.persons}
                label={locales.t('common.reserveModal.persons')}
              />
            )) || (
              <Field
                disabled={disabled}
                name="persons"
                component={ReservePersonSelect}
                maxCapacity={maxCapacity}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const { onReservePropertyChange } = this.props;
                  const { name, value } = e.target;

                  onReservePropertyChange(name, value);
                  getFreeTables({ value, type: name });

                  if (getCalculatedPrepayAmount) {
                    getCalculatedPrepayAmount({ value, type: name });
                  }
                }}
              />
            )}
          </div>
          <div className={classes.fieldStyle}>
            {((readonly) && (
              <TextField
                fullWidth
                name="date"
                value={reserveInfo && moment(reserveInfo.date).format('DD.MM.YYYY')}
                disabled
                label={locales.t('common.reserveModal.selectDate')}
              />
            )) || (
              <Field
                disabled={disabled}
                name="date"
                component={ReserveDatePicker}
                onChange={(date: any) => {
                  const { onReservePropertyChange } = this.props;
                  const value = parseDateForReq(date);

                  onReservePropertyChange('date', value);
                  getFreeTables({ type: 'date', value });

                  if (getCalculatedPrepayAmount) {
                    getCalculatedPrepayAmount({ type: "date", value });
                  }
                }}
              />
            )}
          </div>
          <div className={classes.fieldStyle}>
            {((readonly) && (
              <TextField
                fullWidth
                name="time"
                disabled
                value={reserveInfo && moment(reserveInfo.date).format('HH:mm')}
                label={locales.t('common.reserveModal.time')}
              />
            )) || (
              <Field
                name="time"
                component={ReserveTimeSelect}
                time={timeList || []}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const { onReservePropertyChange } = this.props;
                  const { name, value } = e.target;

                  onReservePropertyChange(name, value);
                  getFreeTables({ value, type: name });

                  if (getCalculatedPrepayAmount) {
                    getCalculatedPrepayAmount({ value, type: name });
                  }
                }}
              />
            )}
          </div>

          {/* <div style={{ paddingTop: 16 }}> */}
          {/* Показываем выбор зала только если дата, время и количество гостей заполнены */}
          {isFieldsFilled && (
            <div className={classes.fieldStyle}>
              <Field
                disabled={disabled}
                name="section"
                component={ReserveSectionSelect}
                options={sections}
                allOptionDisabled={viewType === 'scheme'}
                onChange={this.onPropertyChange}
              />
            </div>
          )}

          {/* Поле выбора стола и кнопка "Выбрать на схеме" - только для мобильных (ширина < 600px) и только если поля заполнены */}
          {isMobileLayout && isFieldsFilled && (
            <>
              {/* Для множественного выбора - TextField с Drawer */}
              {isMergeTables && (
                <>
                  <div className={classes.fieldStyle}>
                    <TextField
                      fullWidth
                      label="Стол"
                      value={
                        selectedTableIds.length > 0
                          ? `Столы ${selectedTableIds
                          .map(id => availableTables?.find(t => t.id === id))
                          .filter(Boolean)
                          .map(table => table!.number)
                          .sort((a, b) => a - b)
                          .join(', ')}`
                          : ''
                      }
                      placeholder="Выберите столы"
                      onClick={() => this.setState({ drawerOpen: true })}
                      disabled={disabled}
                      inputProps={{
                        readOnly: true,
                        style: { cursor: 'pointer' },
                      }}
                      InputLabelProps={{
                        shrink: selectedTableIds.length > 0,
                      }}
                    />
                  </div>

                  {/* Полноэкранный Drawer для выбора столов на мобильных */}
                  <Drawer
                    anchor="bottom"
                    open={this.state.drawerOpen}
                    onClose={() => this.setState({ drawerOpen: false })}
                    variant="temporary"
                    PaperProps={{
                      style: {
                        height: '100vh',
                        maxHeight: '100vh',
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                      },
                    }}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      height="100%"
                      style={{
                        backgroundColor: '#fff',
                      }}
                    >
                      {/* Заголовок с кнопкой закрытия */}
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        padding="16px 20px"
                        borderBottom="1px solid rgba(0, 0, 0, 0.12)"
                      >
                        <Box fontSize={18} fontWeight={600} color="#181F28">
                          Выберите столы
                        </Box>
                        <IconButton
                          onClick={() => this.setState({ drawerOpen: false })}
                          size="small"
                          style={{ padding: 8 }}
                        >
                          <CloseIcon style={{ fontSize: 20 }} />
                        </IconButton>
                      </Box>

                      {/* Прокручиваемый список столов */}
                      <Box
                        flex={1}
                        overflow="auto"
                        padding="8px 0"
                        style={{
                          minHeight: 0,
                        }}
                      >
                        <List>
                          {availableTables.map((table) => {
                            const isSelected = activeTables && activeTables[table.id] !== undefined;
                            return (
                              <ListItem
                                key={table.id}
                                button
                                onClick={() => {
                                  if (handleSelectTable) {
                                    handleSelectTable(table);
                                  }
                                }}
                                style={{
                                  padding: '12px 20px',
                                  backgroundColor: isSelected ? 'rgb(0, 150, 136, 0.15)' : 'transparent',
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = 'rgb(0, 150, 136, 0.1)';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'rgb(0, 150, 136, 0.1)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  } else {
                                    e.currentTarget.style.backgroundColor = 'rgb(0, 150, 136, 0.15)';
                                  }
                                }}
                              >
                                <ListItemText
                                  primary={`Стол ${table.number}`}
                                  style={{
                                    margin: 0,
                                    fontWeight: isSelected ? 500 : 400,
                                  }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>

                      {/* Фиксированная кнопка "Готово" внизу */}
                      <Box
                        padding="16px 20px"
                        borderTop="1px solid rgba(0, 0, 0, 0.12)"
                        style={{
                          backgroundColor: '#fff',
                          boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          onClick={() => this.setState({ drawerOpen: false })}
                          style={{
                            height: 48,
                            fontSize: 16,
                            fontWeight: 600,
                          }}
                        >
                          Готово {selectedTableIds.length > 0 && `(${selectedTableIds.length})`}
                        </Button>
                      </Box>
                    </Box>
                  </Drawer>
                </>
              )}

              {/* Для одиночного выбора - TextField с Drawer */}
              {!isMergeTables && (
                <>
                  <div className={classes.fieldStyle}>
                    <TextField
                      fullWidth
                      label="Стол"
                      value={
                        selectedTableIds[0]
                          ? `Стол ${availableTables?.find(t => t.id === selectedTableIds[0])?.number || ''}`
                          : ''
                      }
                      placeholder="Выберите стол"
                      onClick={() => this.setState({ drawerOpenSingle: true })}
                      disabled={disabled}
                      inputProps={{
                        readOnly: true,
                        style: { cursor: 'pointer' },
                      }}
                      InputLabelProps={{
                        shrink: selectedTableIds.length > 0,
                      }}
                    />
                  </div>

                  {/* Полноэкранный Drawer для одиночного выбора столов на мобильных */}
                  <Drawer
                    anchor="bottom"
                    open={this.state.drawerOpenSingle}
                    onClose={() => this.setState({ drawerOpenSingle: false })}
                    variant="temporary"
                    PaperProps={{
                      style: {
                        height: '100vh',
                        maxHeight: '100vh',
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                      },
                    }}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      height="100%"
                      style={{
                        backgroundColor: '#fff',
                      }}
                    >
                      {/* Заголовок с кнопкой закрытия */}
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        padding="16px 20px"
                        borderBottom="1px solid rgba(0, 0, 0, 0.12)"
                      >
                        <Box fontSize={18} fontWeight={600} color="#181F28">
                          Выберите стол
                        </Box>
                        <IconButton
                          onClick={() => this.setState({ drawerOpenSingle: false })}
                          size="small"
                          style={{ padding: 8 }}
                        >
                          <CloseIcon style={{ fontSize: 20 }} />
                        </IconButton>
                      </Box>

                      {/* Прокручиваемый список столов */}
                      <Box
                        flex={1}
                        overflow="auto"
                        padding="8px 0"
                        style={{
                          minHeight: 0,
                        }}
                      >
                        <List>
                          {availableTables.map((table) => {
                            const isSelected = selectedTableIds[0] === table.id;
                            return (
                              <ListItem
                                key={table.id}
                                button
                                onClick={() => {
                                  if (handleSelectTable) {
                                    handleSelectTable(table);
                                    // Закрываем Drawer после выбора
                                    setTimeout(() => {
                                      this.setState({ drawerOpenSingle: false });
                                    }, 100);
                                  }
                                }}
                                style={{
                                  padding: '12px 20px',
                                  backgroundColor: isSelected ? 'rgb(0, 150, 136, 0.15)' : 'transparent',
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = 'rgb(0, 150, 136, 0.1)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                              >
                                <ListItemText
                                  primary={`Стол ${table.number}`}
                                  style={{
                                    margin: 0,
                                    fontWeight: isSelected ? 500 : 400,
                                  }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    </Box>
                  </Drawer>
                </>
              )}
              <div className={classes.fieldStyle}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={onOpenTableSelectModal}
                  disabled={disabled || !freeTables || (Array.isArray(freeTables) && freeTables.length === 0)}
                >
                  Выбрать на схеме
                </Button>
              </div>
              <div className={classes.fieldStyle}>
                <CircleCheckBox
                  label={locales.t('common.reserveModal.tableMerge')}
                  value={isMergeTables}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const { onReservePropertyChange } = this.props;
                    const { checked } = e.target;

                    onReservePropertyChange('isMergeTables', checked);
                    handleMergeTables();
                  }}
                  isClickableLabel
                  disabled={disabled || disabledEditBanquet}
                />
              </div>
            </>
          )}

          {/* <div className={classes.fieldStyle}> */}
          {/*
          <div style={{ paddingTop: 14 }}>
            <Field
              name="name"
              component={TextFieldComp}
              staticValue={!canEdited && name}
              label={locales.t('common.reserveModal.name')}
              disabled={disabledEditBanquet}
            />
          </div>
          */}

          <Box mt={3}>
            <Field
              name="firstName"
              component={TextFieldComp}
              staticValue={!canEdited && firstName}
              disabled={disabled || disabledEditBanquet}
              label={locales.t('common.reserveModal.firstName')}
              required
              onChange={this.onPropertyChange}
            />
            {/*
            <Box width="50%" mr={1}>
              <Field
                name="firstName"
                component={TextFieldComp}
                staticValue={!canEdited && firstName}
                disabled={disabledEditBanquet}
                label={locales.t('common.reserveModal.firstName')}
                required
                onChange={this.onPropertyChange}
              />
            </Box>
            <Box width="50%">
              <Field
                name="lastName"
                component={TextFieldComp}
                staticValue={!canEdited && lastName}
                label={locales.t('common.reserveModal.lastName')}
                disabled={disabledEditBanquet}
                onChange={this.onPropertyChange}
              />
            </Box>
            */}
          </Box>

          <div className={classes.fieldStyle}>
            <Field
              name="phone"
              component={PhoneFieldComp}
              staticValue={!canEdited && phone}
              label={locales.t('common.reserveModal.phone')}
              locale={locale}
              disabled={disabled || disabledEditBanquet}
              required
              onChange={(value: any) => {
                const { onReservePropertyChange } = this.props;
                onReservePropertyChange('phone', value);
              }}
            />
          </div>

          <Box mt={2} mb={4}>
            <Field
              name="prepayAmount"
              component={TextFieldComp}
              type="number"
              label={locales.t('common.reserveModal.prepayAmount')}
              onChange={this.onPropertyChange}
              // normalize работает только со значением в redux store,
              // но браузер в input type="number" сохраняет введённый текст (например, "007").
              // onBlur принудительно обновляет отображение после потери фокуса.
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                const normalized = normalizePositiveNumber(e.target.value);
                e.target.value = String(normalized);
              }}
              disabled={type === reserveModal.EDIT}
              endAdornment={<InputAdornment position="end">₽</InputAdornment>}
              normalize={(value: string | number | null) => normalizePositiveNumber(value)}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Box>

          {/* <div className={classes.fieldStyle}> */}
          {/* Опция "Объединить столы" перенесена под поле выбора стола для мобильных */}
          {!isMobileLayout && (
            <Box mt={5}>
              <CircleCheckBox
                label={locales.t('common.reserveModal.tableMerge')}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const { onReservePropertyChange } = this.props;
                  const { checked } = e.target;

                  onReservePropertyChange('isMergeTables', checked);
                  handleMergeTables();
                }}
                value={isMergeTables}
                isClickableLabel
                disabled={disabled || disabledEditBanquet}
              />
            </Box>
          )}

          <div style={{ paddingTop: 4 }}>
            <Field
              name="isBanquet"
              component={banquetCheckBox}
              disabled={disabled || disabledEditBanquet}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const { onReservePropertyChange } = this.props;
                const { checked } = e.target;

                onReservePropertyChange('isBanquet', checked);
              }}
            />
          </div>

          {/* <div className={classes.fieldStyle}> */}
          <Box mt={2}>
            <Field
              name="comment"
              component={TextAreaComp}
              rows={4}
              staticValue={type === reserveModal.CONFIRM}
              multiline
              label={locales.t('common.reserveModal.comment')}
              disabled={disabled || disabledEditBanquet}
              onChange={this.onPropertyChange}
            />
          </Box>

          {/*
          <div className={classes.fieldStyle}>
            <FormControl>
              <FormLabel style={{ fontSize: 10, marginBottom: 6 }}>
                {locales.t('common.reserveModal.duration')}
              </FormLabel>
              <div className={classes.durationFieldStyle}>
                <div style={{ marginRight: 12, width: '100%' }}>
                  <Field
                    name="hours"
                    component={TextFieldComp}
                    type="number"
                    onChange={
                      (e: ChangeEvent<HTMLSelectElement>) => getFreeTables({
                        value: e.target.value,
                        type: e.target.name,
                      })
                    }
                    staticValue={type === reserveModal.CONFIRM}
                    normalize={normalizeDurationHours}
                    endAdornment={(
                      <InputAdornment
                        position="end"
                      >
                        час.
                      </InputAdornment>
                    )}
                    disabled={disabledEditBanquet}
                  />
                </div>
                {/~~*
                <Field
                  name="minutes"
                  component={TextFieldComp}
                  type="number"
                  onChange={
                    (e: ChangeEvent<HTMLSelectElement>) => getFreeTables({ value: e.target.value, type: e.target.name })
                  }
                  staticValue
                  endAdornment={<InputAdornment position="end">мин.</InputAdornment>}
                />
                *~~/}
                <TextField
                  fullWidth
                  value={minutes || 0}
                  type={type}
                  disabled
                  InputProps={{
                    endAdornment: <InputAdornment position="end">мин.</InputAdornment>,
                  }}
                />
              </div>
            </FormControl>
          </div>
          */}

          <Box mt={2} mb={4}>
            <FormControl>
              <FormLabel style={{ fontSize: 10 }}>
                {locales.t('common.reserveModal.duration')}
              </FormLabel>
              <Box display="flex">
                <Box width="50%" mr={1}>
                  <Field
                    name="hours"
                    component={TextFieldComp}
                    type="number"
                    onValueChange={(value: string, name: string) => {
                      const { onReservePropertyChange } = this.props;

                      onReservePropertyChange(name, value);
                      getFreeTables({ value, type: name });
                    }}
                    handleOnBlur={normalizeDurationHours}
                    staticValue={type === reserveModal.CONFIRM}
                    endAdornment={<InputAdornment position="end">час.</InputAdornment>}
                    disabled={disabled || disabledEditBanquet}
                    errorAsHelperText
                    inputProps={{ min: 0, max: 23 }}
                  />
                </Box>
                <Box width="50%" mr={1}>
                  <Field
                    name="minutes"
                    component={TextFieldComp}
                    type="number"
                    onValueChange={(value: string, name: string) => {
                      const { onReservePropertyChange } = this.props;

                      onReservePropertyChange(name, value);
                      getFreeTables({ value, type: name });
                    }}
                    handleOnBlur={normalizeToNearest5}
                    staticValue={type === reserveModal.CONFIRM}
                    endAdornment={<InputAdornment position="end">мин.</InputAdornment>}
                    disabled={disabled || disabledEditBanquet}
                    errorAsHelperText
                    inputProps={{ step: 5, min: 0, max: 55 }}
                  />
                </Box>
              </Box>
            </FormControl>
          </Box>
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(ReservesDataFields);
